import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Alert,
  AlertTitle,
  Chip,
  Button,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton
} from '@mui/material';
import {
  Build as BuildIcon,
  AutoAwesome as AutoIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Lightbulb as LightbulbIcon
} from '@mui/icons-material';
import ChatPanel from './ChatPanel';
import PlanView from './PlanView';
import CheckpointView from './CheckpointView';
import ProgressIndicator from './ProgressIndicator';
import useMultiPhaseFlowBuilder from '../../hooks/useMultiPhaseFlowBuilder';
import api from '../../api/axios';

/**
 * MultiPhaseChatPanel Component
 * NEW: Automatic mode detection - no more manual toggle!
 * NEW: Feature gap detection with suggestions
 * NEW: Per-step progress tracking
 */
const MultiPhaseChatPanel = ({ session, currentFlow, onFlowGenerated, onSessionNameChange }) => {
  // Mode is now auto-detected, not user-selected
  const [detectedMode, setDetectedMode] = useState(null); // 'standard', 'multi-phase', 'feature_gap'
  const [showMultiPhaseUI, setShowMultiPhaseUI] = useState(false);
  const [currentBuildingStep, setCurrentBuildingStep] = useState(null);
  const [featureGaps, setFeatureGaps] = useState([]);
  const [showFeatureGaps, setShowFeatureGaps] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  const {
    plan,
    planId,
    checkpoint,
    loading,
    error,
    phase,
    createPlan,
    modifyPlan,
    buildBatch,
    cancelPlan,
    reset,
    setPlanFromAnalysis // NEW: Set plan from analysis result
  } = useMultiPhaseFlowBuilder(session?.sessionId);

  // Detect when to switch to multi-phase UI
  useEffect(() => {
    // When building is complete, switch back to chat mode
    if (phase === 'completed') {
      console.log('✅ [MultiPhase] Building complete, switching back to chat mode');
      setShowMultiPhaseUI(false);
      setDetectedMode(null);
      return;
    }
    
    if (detectedMode === 'multi-phase' && (plan || checkpoint)) {
      setShowMultiPhaseUI(true);
    } else if (detectedMode === 'feature_gap') {
      setShowMultiPhaseUI(false);
      setShowFeatureGaps(true);
    } else {
      setShowMultiPhaseUI(false);
    }
  }, [detectedMode, plan, checkpoint, phase]);

  // Intelligent request analysis - auto-detects mode
  const analyzeRequest = async (message) => {
    if (!session?.sessionId) return null;
    
    setIsAnalyzing(true);
    try {
      const response = await api.post('/ai-flow-builder/analyze', {
        sessionId: session.sessionId,
        message
      });
      
      const result = response.data;
      console.log('🔍 [Analysis] Result:', result);
      
      setAnalysisResult(result);
      
      if (result.hasFeatureGaps) {
        setFeatureGaps(result.featureGaps);
        setShowFeatureGaps(true);
        setDetectedMode('feature_gap');
      }
      // Don't set detectedMode here for multi-phase - let handleMessageSend do it after updating hook state
      
      return result;
    } catch (error) {
      console.error('Error analyzing request:', error);
      // Fall back to standard mode on error
      setDetectedMode('standard');
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Handle message interception for analysis
  const handleMessageSend = async (message) => {
    // First, analyze the request to determine mode
    const analysis = await analyzeRequest(message);
    
    if (!analysis) {
      // Analysis failed, let standard chat handle it
      return null;
    }
    
    // If multi-phase mode with a plan, update the hook's state and show PlanView
    if (analysis.mode === 'multi-phase' && analysis.planId && analysis.plan) {
      console.log('📋 [MultiPhase] Plan received from analysis, updating hook state...');
      
      // CRITICAL: Update the hook's state with the plan data FIRST
      setPlanFromAnalysis(analysis);
      
      // THEN set the detected mode (triggers the effect to show multi-phase UI)
      setDetectedMode('multi-phase');
      
      // Trigger a reload of the chat session to show the messages
      window.dispatchEvent(new CustomEvent('reloadChatSession'));
      return analysis;
    }
    
    // If feature gaps detected, show them (handled by state update in analyzeRequest)
    if (analysis.mode === 'feature_gap') {
      return analysis;
    }
    
    // Standard mode - set mode and let the regular chat handle it
    setDetectedMode('standard');
    return null;
  };

  // Handle plan modification
  const handlePlanModify = async (modifications) => {
    try {
      const result = await modifyPlan(modifications);
      
      // Reload chat session to show updated plan
      window.dispatchEvent(new CustomEvent('reloadChatSession'));
      
      return result;
    } catch (error) {
      console.error('Error modifying plan:', error);
      throw error;
    }
  };

  // Handle plan approval - now with per-step progress and auto-continue
  const handlePlanApprove = async () => {
    try {
      let hasMore = true;
      let stepIndex = 0;
      
      while (hasMore) {
        // Show which step is being built
        if (plan && plan.steps && plan.steps[stepIndex]) {
          const currentStep = plan.steps[stepIndex];
          setCurrentBuildingStep(`Building step ${stepIndex + 1}/${plan.steps.length}: ${currentStep.name}`);
        }
        
        const result = await buildBatch();
        
        // Display partial flow in canvas immediately after each step
        if (result && result.flow && onFlowGenerated) {
          console.log(`🎨 [Multi-Phase] Displaying step ${stepIndex + 1} in canvas...`);
          onFlowGenerated(result.flow);
        }
        
        // Check if there are more steps to build
        hasMore = result?.checkpoint?.hasMore || false;
        stepIndex = result?.checkpoint?.progress?.completed || (stepIndex + 1);
        
        // Small delay for UI to update
        if (hasMore) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      setCurrentBuildingStep(null);
      console.log('✅ [Multi-Phase] All steps built successfully!');
      
    } catch (error) {
      console.error('Error building batch:', error);
      setCurrentBuildingStep(null);
    }
  };

  // Handle checkpoint continue - auto-continues building all remaining steps
  const handleCheckpointContinue = async () => {
    try {
      let hasMore = true;
      let stepIndex = checkpoint?.progress?.completed || 0;
      
      while (hasMore) {
        // Show which step is being built next
        if (plan && plan.steps && plan.steps[stepIndex]) {
          setCurrentBuildingStep(`Building step ${stepIndex + 1}/${plan.steps.length}: ${plan.steps[stepIndex].name}`);
        }
        
        const result = await buildBatch();
        
        // Update canvas with latest built steps immediately
        if (result && result.flow && onFlowGenerated) {
          console.log(`🎨 [Multi-Phase] Displaying step ${stepIndex + 1} in canvas...`);
          onFlowGenerated(result.flow);
        }
        
        // Check if there are more steps to build
        hasMore = result?.checkpoint?.hasMore || false;
        stepIndex = result?.checkpoint?.progress?.completed || (stepIndex + 1);
        
        // Small delay for UI to update
        if (hasMore) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      setCurrentBuildingStep(null);
      console.log('✅ [Multi-Phase] All remaining steps built successfully!');
      
    } catch (error) {
      console.error('Error building batch:', error);
      setCurrentBuildingStep(null);
    }
  };

  // Handle plan/checkpoint cancel/stop
  const handleCancel = async () => {
    try {
      await cancelPlan();
      reset();
      setDetectedMode(null);
      setAnalysisResult(null);
    } catch (error) {
      console.error('Error cancelling plan:', error);
    }
  };

  // Handle feature gap acknowledgment
  const handleFeatureGapAcknowledge = () => {
    setShowFeatureGaps(false);
    setFeatureGaps([]);
    setDetectedMode(null);
    // Let user try again with the suggestions in mind
  };

  // Calculate progress
  const getProgress = () => {
    // If building but no checkpoint yet (first batch), show initial progress
    if (phase === 'building' && !checkpoint && plan) {
      return {
        completed: 0,
        total: plan.totalSteps,
        pending: plan.totalSteps,
        errors: 0
      };
    }
    
    if (!checkpoint) {
      return null;
    }

    return {
      completed: checkpoint.progress.completed,
      total: checkpoint.progress.total,
      pending: checkpoint.progress.total - checkpoint.progress.completed,
      errors: 0
    };
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Intelligent Analysis Indicator - replaces mode selector */}
      <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoIcon sx={{ color: '#9333ea', fontSize: '1.2rem' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              AI Flow Builder
            </Typography>
          </Box>
          
          {/* Mode Indicator */}
          {detectedMode && (
            <Chip 
              size="small"
              label={detectedMode === 'multi-phase' ? 'Multi-Phase' : detectedMode === 'feature_gap' ? 'Feature Gap' : 'Standard'}
              color={detectedMode === 'feature_gap' ? 'warning' : 'primary'}
              variant="outlined"
              icon={detectedMode === 'feature_gap' ? <WarningIcon /> : <CheckCircleIcon />}
            />
          )}
          
          {/* Progress Indicator */}
          {(getProgress() || phase === 'building') && (
            <ProgressIndicator 
              progress={getProgress() || { completed: 0, total: plan?.totalSteps || 0, pending: plan?.totalSteps || 0, errors: 0 }} 
              currentStep={currentBuildingStep || (loading ? 'Building...' : null)}
              compact 
            />
          )}
        </Box>

        {/* Analysis in progress */}
        {isAnalyzing && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              🔍 Analyzing your request...
            </Typography>
            <LinearProgress sx={{ height: 2, borderRadius: 1 }} />
          </Box>
        )}

        {/* Per-step building progress */}
        {currentBuildingStep && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <BuildIcon sx={{ fontSize: '0.9rem' }} />
              {currentBuildingStep}
            </Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2, py: 0.5 }}>
            <Typography variant="caption">{error}</Typography>
          </Alert>
        )}
      </Paper>

      {/* Feature Gap Alert */}
      <Collapse in={showFeatureGaps && featureGaps.length > 0}>
        <Paper elevation={3} sx={{ p: 2, mb: 2, bgcolor: '#fffbeb', border: '1px solid #f59e0b' }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <WarningIcon sx={{ color: '#f59e0b', mt: 0.3 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#92400e' }}>
                Feature Gaps Detected
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Some requested features aren't directly available. Here are alternatives:
              </Typography>
              
              <List dense sx={{ py: 0 }}>
                {featureGaps.map((gap, index) => (
                  <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <LightbulbIcon sx={{ fontSize: '1rem', color: '#d97706' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          "{gap.requested}"
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {gap.suggestion}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>

              <Button 
                size="small" 
                variant="outlined" 
                onClick={handleFeatureGapAcknowledge}
                sx={{ mt: 1, textTransform: 'none' }}
              >
                Got it, I'll try a different approach
              </Button>
            </Box>
          </Box>
        </Paper>
      </Collapse>

      {/* Content Area */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {showMultiPhaseUI ? (
          <Box>
            {/* Show Plan View */}
            {phase === 'planned' && plan && !checkpoint && (
              <PlanView
                plan={plan}
                planId={planId}
                onApprove={handlePlanApprove}
                onModify={handlePlanModify}
                onCancel={handleCancel}
                loading={loading}
              />
            )}

            {/* Show Checkpoint View */}
            {checkpoint && (
              <CheckpointView
                checkpoint={{
                  ...checkpoint,
                  builtSteps: checkpoint.builtSteps || []
                }}
                planId={planId}
                onContinue={handleCheckpointContinue}
                onStop={handleCancel}
                loading={loading}
              />
            )}

            {/* Show Loading State */}
            {phase === 'planning' && !plan && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  📋 Creating plan...
                </Typography>
              </Box>
            )}
            
            {/* Show Building State with Per-Step Progress */}
            {phase === 'building' && loading && plan && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                  🔨 Building Your Flow
                </Typography>
                
                {/* Per-step progress list */}
                <Paper elevation={1} sx={{ p: 2, mx: 2, mb: 2, textAlign: 'left' }}>
                  <List dense>
                    {plan.steps.map((step, index) => {
                      const progress = getProgress();
                      const isCompleted = progress && index < progress.completed;
                      const isCurrent = currentBuildingStep && currentBuildingStep.includes(step.name);
                      const isPending = !isCompleted && !isCurrent;
                      
                      return (
                        <ListItem key={index} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {isCompleted ? (
                              <CheckCircleIcon sx={{ color: 'success.main', fontSize: '1.1rem' }} />
                            ) : isCurrent ? (
                              <BuildIcon sx={{ color: 'primary.main', fontSize: '1.1rem' }} className="pulse" />
                            ) : (
                              <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid #e0e0e0' }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: isCurrent ? 600 : 400,
                                  color: isPending ? 'text.secondary' : 'text.primary'
                                }}
                              >
                                {step.name}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {step.type}
                              </Typography>
                            }
                          />
                          {isCurrent && (
                            <Chip size="small" label="Building..." color="primary" variant="outlined" />
                          )}
                        </ListItem>
                      );
                    })}
                  </List>
                </Paper>
                
                <Box sx={{ px: 4 }}>
                  <ProgressIndicator 
                    progress={getProgress() || { completed: 0, total: plan.totalSteps, pending: plan.totalSteps, errors: 0 }}
                    currentStep={currentBuildingStep}
                  />
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <ChatPanel
            session={session}
            currentFlow={currentFlow}
            onFlowGenerated={onFlowGenerated}
            onSessionNameChange={onSessionNameChange}
            onMessageSend={handleMessageSend}
          />
        )}
      </Box>

      {/* Inline styles for animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        .pulse {
          animation: pulse 1.5s infinite;
        }
      `}</style>
    </Box>
  );
};

export default MultiPhaseChatPanel;
